import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Edit, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, useAuth } from '@/contexts/AuthContext';
import { PageCover, PageBody, SheetHeader } from '@/components/Page';

// Ficha da filial: seção com título pequeno e pares rótulo/valor.
const Secao = ({ titulo, children }) => (
  <section className="px-6 py-4">
    <h3 className="mb-1.5 text-[12.5px] font-medium text-gray-500">{titulo}</h3>
    {children}
  </section>
);

const Campo = ({ rotulo, children, mono = false }) => (
  <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-3 py-1.5 text-sm">
    <dt className="text-gray-500">{rotulo}</dt>
    <dd className={`min-w-0 break-words ${mono ? 'font-mono text-[13px]' : ''}`}>{children || '—'}</dd>
  </div>
);

const Companies = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    nome_fantasia: '',
    cnpj: '',
    cidade: '',
    estado: '',
    cep: '',
    endereco: '',
    telefone: '',
    email: '',
    pix_cnpj: '',
    pix_email: '',
    pix_telefone: '',
    pix_chave_aleatoria: '',
    observacoes: '',
    horario_funcionamento: '',
    token_api: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/empresas`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setFormData({
      nome: company.nome || '',
      nome_fantasia: company.nome_fantasia || '',
      cnpj: company.cnpj || '',
      cidade: company.cidade || '',
      estado: company.estado || '',
      cep: company.cep || '',
      endereco: company.endereco || '',
      telefone: company.telefone || '',
      email: company.email || '',
      pix_cnpj: company.pix_cnpj || '',
      pix_email: company.pix_email || '',
      pix_telefone: company.pix_telefone || '',
      pix_chave_aleatoria: company.pix_chave_aleatoria || '',
      observacoes: company.observacoes || '',
      horario_funcionamento: company.horario_funcionamento || '',
      token_api: '' // Não mostramos o token por segurança
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== selectedCompany[key]) {
          updateData[key] = formData[key];
        }
      });

      await axios.patch(`${API_URL}/empresas/${selectedCompany.id}`, updateData);
      
      toast.success('Empresa atualizada com sucesso!');
      setEditDialogOpen(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar empresa');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = user?.role === 'master';

  const fichaRef = useRef(null);
  const selecionada = companies.find((c) => c.id === expandedCard) || companies[0];
  const temPix = selecionada && (selecionada.pix_cnpj || selecionada.pix_email || selecionada.pix_telefone || selecionada.pix_chave_aleatoria);

  return (
    <div data-testid="companies-page">
      <PageCover title="Empresas" subtitle={canEdit ? 'Filiais do sistema: contato, endereço e chaves PIX' : 'Informações das filiais'} />
      <PageBody>
        {loading ? (
          <Card>
            <ul aria-hidden className="divide-y divide-border">
              {[1, 2, 3].map((i) => (
                <li key={i} className="space-y-2 px-5 py-4">
                  <div className="h-4 w-48 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-32 animate-pulse rounded bg-muted" />
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <Card className="overflow-hidden">
              <SheetHeader title={`Filiais · ${companies.length}`} />
              <ul className="divide-y divide-border">
                {companies.map((company) => {
                  const ativa = selecionada?.id === company.id;
                  return (
                    <li key={company.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedCard(company.id);
                          if (window.innerWidth < 1024) fichaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        aria-current={ativa || undefined}
                        data-testid={`company-card-${company.id}`}
                        className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors ${ativa ? 'bg-accent' : 'hover:bg-muted/50'}`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{company.nome_fantasia}</span>
                          <span className="block text-[13px] text-gray-500">{company.cidade}, {company.estado}</span>
                        </span>
                        {company.is_matriz && <span className="shrink-0 text-[12.5px] text-gray-500">Matriz</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>

            {selecionada && (
              <Card ref={fichaRef} className="overflow-hidden lg:sticky lg:top-6">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-5">
                  <div className="min-w-0">
                    <p className="text-[12.5px] text-gray-500">{selecionada.is_matriz ? 'Matriz' : 'Filial'} · CNPJ {selecionada.cnpj}</p>
                    <h2 className="mt-1 text-[22px] font-medium leading-tight tracking-[-0.01em]">{selecionada.nome_fantasia}</h2>
                    <p className="mt-0.5 text-[13.5px] text-gray-500">{selecionada.cidade}, {selecionada.estado}</p>
                  </div>
                  {canEdit && (
                    <Button variant="outline" size="sm" onClick={() => handleEdit(selecionada)} data-testid={`edit-company-${selecionada.id}`}>
                      <Edit className="mr-1.5 h-4 w-4" /> Editar
                    </Button>
                  )}
                </div>
                <div className="divide-y divide-border">
                  <Secao titulo="Contato">
                    <dl>
                      <Campo rotulo="Telefone">{selecionada.telefone}</Campo>
                      <Campo rotulo="Email">{selecionada.email}</Campo>
                    </dl>
                  </Secao>
                  <Secao titulo="Endereço">
                    <dl>
                      <Campo rotulo="Logradouro">{selecionada.endereco}</Campo>
                      <Campo rotulo="Cidade">{selecionada.cidade}, {selecionada.estado}</Campo>
                      <Campo rotulo="CEP">{selecionada.cep}</Campo>
                    </dl>
                  </Secao>
                  {temPix && (
                    <Secao titulo="Chaves PIX">
                      <dl>
                        {selecionada.pix_cnpj && <Campo rotulo="CNPJ" mono>{selecionada.pix_cnpj}</Campo>}
                        {selecionada.pix_email && <Campo rotulo="Email" mono>{selecionada.pix_email}</Campo>}
                        {selecionada.pix_telefone && <Campo rotulo="Telefone" mono>{selecionada.pix_telefone}</Campo>}
                        {selecionada.pix_chave_aleatoria && <Campo rotulo="Chave aleatória" mono>{selecionada.pix_chave_aleatoria}</Campo>}
                      </dl>
                    </Secao>
                  )}
                  {selecionada.horario_funcionamento && (
                    <Secao titulo="Horário de funcionamento">
                      <p className="text-sm">{selecionada.horario_funcionamento}</p>
                    </Secao>
                  )}
                  {selecionada.observacoes && (
                    <Secao titulo="Observações">
                      <p className="whitespace-pre-line text-sm">{selecionada.observacoes}</p>
                    </Secao>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}
      </PageBody>

      {/* Edit Dialog (Only for Master) */}
      {canEdit && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar empresa</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Informações Básicas */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Informações básicas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome_fantasia">Nome Fantasia</Label>
                    <Input
                      id="nome_fantasia"
                      value={formData.nome_fantasia}
                      onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="nome">Razão Social</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cnpj">CNPJ</Label>
                    <Input
                      id="cnpj"
                      value={formData.cnpj}
                      onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefone">Telefone</Label>
                    <Input
                      id="telefone"
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              {/* Endereço */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Endereço</h3>
                <div>
                  <Label htmlFor="endereco">Rua/Avenida</Label>
                  <Input
                    id="endereco"
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cep">CEP</Label>
                    <Input
                      id="cep"
                      value={formData.cep}
                      onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="estado">Estado</Label>
                    <Input
                      id="estado"
                      value={formData.estado}
                      onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>

              {/* Chaves PIX */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Chaves PIX</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pix_cnpj">PIX - CNPJ</Label>
                    <Input
                      id="pix_cnpj"
                      value={formData.pix_cnpj}
                      onChange={(e) => setFormData({ ...formData, pix_cnpj: e.target.value })}
                      placeholder="00.000.000/0001-00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pix_email">PIX - Email</Label>
                    <Input
                      id="pix_email"
                      value={formData.pix_email}
                      onChange={(e) => setFormData({ ...formData, pix_email: e.target.value })}
                      placeholder="email@empresa.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pix_telefone">PIX - Telefone</Label>
                    <Input
                      id="pix_telefone"
                      value={formData.pix_telefone}
                      onChange={(e) => setFormData({ ...formData, pix_telefone: e.target.value })}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pix_chave_aleatoria">PIX - Chave Aleatória</Label>
                    <Input
                      id="pix_chave_aleatoria"
                      value={formData.pix_chave_aleatoria}
                      onChange={(e) => setFormData({ ...formData, pix_chave_aleatoria: e.target.value })}
                      placeholder="UUID"
                    />
                  </div>
                </div>
              </div>

              {/* Informações Adicionais */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Informações adicionais</h3>
                <div>
                  <Label htmlFor="horario_funcionamento">Horário de Funcionamento</Label>
                  <Input
                    id="horario_funcionamento"
                    value={formData.horario_funcionamento}
                    onChange={(e) => setFormData({ ...formData, horario_funcionamento: e.target.value })}
                    placeholder="Ex: Seg-Sex: 8h às 18h"
                  />
                </div>

                <div>
                  <Label htmlFor="token_api">Token API Tiny ERP</Label>
                  <Input
                    id="token_api"
                    type="password"
                    value={formData.token_api}
                    onChange={(e) => setFormData({ ...formData, token_api: e.target.value })}
                    placeholder="Token de autenticação do Tiny ERP"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Este token será salvo no .env do sistema para autenticação com o Tiny ERP
                  </p>
                </div>

                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Informações adicionais sobre a empresa..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Companies;
